import { useState } from 'react';
import { SoilTestListItem } from '@/api/growth/dashboard/type';

// SW 층별 고정 깊이: SW1D(0-5cm)~SW6D(48-60cm) 하단 기준
const SW_LAYER_DEPTHS = [5, 15, 24, 36, 48, 60];

interface GrowthDashboardPawcCardProps {
    soilTestList: SoilTestListItem[];
    waterState?: string;
}

const catmullRomCurve = (pts: Array<[number, number]>): string => {
    if (pts.length < 2) return '';
    const ext = [pts[0], ...pts, pts[pts.length - 1]];
    let d = '';
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = ext[i], p1 = ext[i + 1], p2 = ext[i + 2], p3 = ext[i + 3];
        const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
        const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
        const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
        const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
    }
    return d;
};

const SVG_W = 580;
const SVG_H = 620;
const M = { top: 58, right: 30, bottom: 108, left: 68 };
const CW = SVG_W - M.left - M.right;   // 482
const CH = SVG_H - M.top - M.bottom;   // 454
const X_MIN = 0.0;
const X_MAX = 0.8;
const X_TICKS = [0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8];

const C = {
    ll:        '#FF4560',
    dul:       '#00C37A',
    sat:       '#008FFB',
    sw:        '#FEB019',
    grid:      '#E8E8E8',
    axisLine:  '#CCCCCC',
    axisTick:  '#9BAABF',
    axisLabel: '#64748B',
    axisTitle: '#334155',
};

interface TooltipState {
    x: number; y: number;
    label: string; value: number; depth: number; color: string;
}

const GrowthDashboardPawcCard = ({
    soilTestList,
    waterState,
}: GrowthDashboardPawcCardProps) => {
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);

    if (!soilTestList || soilTestList.length === 0) {
        return (
            <div className="card custom-card p-2 h-100 d-flex flex-column">
                <div className="card-header flex-shrink-0">
                    <h4 className="mb-0">토심에 따른 토양 수분 특성 곡선과 PAWC범위</h4>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center flex-column p-5" style={{ flex: 1 }}>
                    <i className="bi bi-droplet text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }} />
                    <h6 className="text-muted mt-3 mb-1">토양 정보 없음</h6>
                    <p className="text-muted small mb-0">토양 분석 데이터가 없습니다</p>
                </div>
            </div>
        );
    }

    const depths = soilTestList.map(s => s.depth);
    const maxDepth = Math.max(...depths);
    const Y_MAX = Math.ceil(maxDepth / 30) * 30;

    const xScale = (v: number) => M.left + ((v - X_MIN) / (X_MAX - X_MIN)) * CW;
    const yScale = (d: number) => M.top + (d / Y_MAX) * CH;

    const llVals  = soilTestList.map(s => s.droughtPoint);
    const dulVals = soilTestList.map(s => s.drainLimitPoint);
    const satVals = soilTestList.map(s => s.floodPoint);

    const buildPts = (vals: number[]): Array<[number, number]> => {
        const pts: Array<[number, number]> = [[xScale(vals[0]), yScale(0)]];
        depths.forEach((d, i) => pts.push([xScale(vals[i]), yScale(d)]));
        return pts;
    };

    const llPts  = buildPts(llVals);
    const dulPts = buildPts(dulVals);
    const satPts = buildPts(satVals);

    const swVals: number[] = waterState
        ? waterState.split('|').map(v => parseFloat(v.trim())).filter(v => !isNaN(v))
        : [];

    const swPts: Array<[number, number]> = (() => {
        if (swVals.length === 0) return [];
        const pts: Array<[number, number]> = [[xScale(swVals[0]), yScale(0)]];
        SW_LAYER_DEPTHS.forEach((depth, i) => {
            if (i < swVals.length && depth <= Y_MAX)
                pts.push([xScale(swVals[i]), yScale(depth)]);
        });
        return pts;
    })();

    const toPath = (pts: Array<[number, number]>) =>
        `M ${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}` + catmullRomCurve(pts);

    // PAWC 폴리곤 (DUL → LL 역방향 → 닫기)
    const llRev = [...llPts].reverse();
    const pawcPath =
        `M ${dulPts[0][0].toFixed(1)},${dulPts[0][1].toFixed(1)}` +
        catmullRomCurve(dulPts) +
        ` L ${llRev[0][0].toFixed(1)},${llRev[0][1].toFixed(1)}` +
        catmullRomCurve(llRev) + ' Z';

    const yTicks = [0, ...depths];
    const hasSw  = swPts.length >= 2;

    // ── 툴팁 렌더링 ──────────────────────────────────────────
    const renderTooltip = () => {
        if (!tooltip) return null;
        const TW = 186;
        const TH = 76;
        const ACCENT = 5;
        const tx = tooltip.x + TW + 18 > M.left + CW
            ? tooltip.x - TW - 14
            : tooltip.x + 14;
        const ty = tooltip.y - TH - 14 < M.top
            ? tooltip.y + 14
            : tooltip.y - TH - 14;

        return (
            <g style={{ pointerEvents: 'none' }}>
                {/* Crosshair */}
                <line x1={tooltip.x} y1={M.top} x2={tooltip.x} y2={M.top + CH}
                    stroke={tooltip.color} strokeWidth="1" strokeDasharray="5,4" opacity="0.35" />
                <line x1={M.left} y1={tooltip.y} x2={M.left + CW} y2={tooltip.y}
                    stroke={tooltip.color} strokeWidth="1" strokeDasharray="5,4" opacity="0.35" />

                {/* Shadow */}
                <rect x={tx + 2} y={ty + 4} width={TW} height={TH} rx="10"
                    fill="rgba(30,40,60,0.12)" />
                {/* Background */}
                <rect x={tx} y={ty} width={TW} height={TH} rx="10" fill="white" />
                {/* Left accent bar */}
                <rect x={tx} y={ty} width={ACCENT} height={TH} rx="3" fill={tooltip.color} />
                <rect x={tx + ACCENT - 3} y={ty} width={3} height={TH} fill={tooltip.color} />

                {/* 레이블 */}
                <text x={tx + ACCENT + 10} y={ty + 22}
                    fontSize="11.5" fontWeight="700" fill={tooltip.color}>
                    {tooltip.label}
                </text>
                {/* 구분선 */}
                <line x1={tx + ACCENT + 8} y1={ty + 30}
                    x2={tx + TW - 10} y2={ty + 30}
                    stroke="#EEF2F8" strokeWidth="1" />
                {/* 토심 */}
                <text x={tx + ACCENT + 10} y={ty + 48}
                    fontSize="10" fill="#94A3B8">토심</text>
                <text x={tx + TW - 10} y={ty + 48}
                    textAnchor="end" fontSize="10.5" fontWeight="600" fill="#334155">
                    {tooltip.depth} cm
                </text>
                {/* 수분값 */}
                <text x={tx + ACCENT + 10} y={ty + 66}
                    fontSize="10" fill="#94A3B8">체적수분함량</text>
                <text x={tx + TW - 10} y={ty + 66}
                    textAnchor="end" fontSize="11" fontWeight="700" fill="#1E293B">
                    {tooltip.value.toFixed(3)} mm/mm
                </text>
            </g>
        );
    };

    // ── 데이터 포인트 (ApexCharts markers 스타일: size=4, strokeWidth=2, hover=6) ──
    const mkPt = (
        cx: number, cy: number, color: string,
        label: string, value: number, depth: number
    ) => (
        <g key={`${label}-${depth}`}>
            {/* Hit area */}
            <circle cx={cx} cy={cy} r={20} fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTooltip({ x: cx, y: cy, label, value, depth, color })}
                onMouseLeave={() => setTooltip(null)} />
            {/* Marker: white fill + color stroke (ApexCharts style) */}
            <circle cx={cx} cy={cy} r={4} fill="white" stroke={color} strokeWidth="2" />
        </g>
    );

    const legendY = M.top + CH + 20;

    // ── 범례 아이템 ──────────────────────────────────────────
    const mkLegendLine = (x: number, y: number, color: string, label: string, dash?: string) => (
        <g>
            <line x1={x} y1={y} x2={x + 22} y2={y}
                stroke={color} strokeWidth="2"
                strokeDasharray={dash ?? 'none'} strokeLinecap="round" />
            <circle cx={x + 11} cy={y} r="3" fill="white" stroke={color} strokeWidth="2" />
            <text x={x + 28} y={y + 4} fontSize="10.5" fill="#475569" fontWeight="500">{label}</text>
        </g>
    );

    const mkLegendRect = (x: number, y: number, color: string, label: string) => (
        <g>
            <rect x={x} y={y - 6} width={22} height={12} rx="3"
                fill={color + '40'} stroke={color} strokeWidth="1.5" />
            <text x={x + 28} y={y + 4} fontSize="10.5" fill="#475569" fontWeight="500">{label}</text>
        </g>
    );

    return (
        <div className="card custom-card p-2 h-100 d-flex flex-column">
            <div className="card-header flex-shrink-0">
                <h4 className="mb-0">토심에 따른 토양 수분 특성 곡선과 PAWC범위</h4>
            </div>
            <div className="card-body p-1"
                style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                <svg
                    viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    preserveAspectRatio="xMidYMid meet"
                    aria-label="토심에 따른 토양 수분 특성 곡선과 PAWC범위 차트"
                >
                    <defs>
                        <clipPath id="chartClip">
                            <rect x={M.left} y={M.top} width={CW} height={CH} />
                        </clipPath>
                    </defs>

                    {/* ── 차트 배경 (흰색) ─────────────────────── */}
                    <rect x={M.left} y={M.top} width={CW} height={CH}
                        fill="white" rx="2" />

                    {/* ── 수직 그리드 (solid) ───────────────────── */}
                    {X_TICKS.map(t => (
                        <line key={`vg-${t}`}
                            x1={xScale(t)} y1={M.top}
                            x2={xScale(t)} y2={M.top + CH}
                            stroke={C.grid} strokeWidth="0.8" />
                    ))}

                    {/* ── 수평 그리드 (solid) ───────────────────── */}
                    {yTicks.map(t => (
                        <line key={`hg-${t}`}
                            x1={M.left} y1={yScale(t)}
                            x2={M.left + CW} y2={yScale(t)}
                            stroke={C.grid} strokeWidth="0.8" />
                    ))}

                    {/* ── PAWC 채움 (단색) ─────────────────────── */}
                    <path d={pawcPath}
                        fill="rgba(76,175,80,0.15)"
                        stroke="none"
                        clipPath="url(#chartClip)" />

                    {/* ── SAT 선 (파랑 dashed: dashArray=5) ───── */}
                    <path d={toPath(satPts)} fill="none"
                        stroke={C.sat} strokeWidth="2"
                        strokeDasharray="5,3" strokeLinecap="round"
                        clipPath="url(#chartClip)" />

                    {/* ── SW 선 (앰버 dashed: dashArray=5) ────── */}
                    {hasSw && (
                        <path d={toPath(swPts)} fill="none"
                            stroke={C.sw} strokeWidth="2"
                            strokeDasharray="5,3" strokeLinecap="round"
                            clipPath="url(#chartClip)" />
                    )}

                    {/* ── DUL 선 (초록) ───────────────────────── */}
                    <path d={toPath(dulPts)} fill="none"
                        stroke={C.dul} strokeWidth="2.5"
                        strokeLinecap="round"
                        clipPath="url(#chartClip)" />

                    {/* ── LL 선 (빨강) ────────────────────────── */}
                    <path d={toPath(llPts)} fill="none"
                        stroke={C.ll} strokeWidth="2.5"
                        strokeLinecap="round"
                        clipPath="url(#chartClip)" />

                    {/* ── 차트 테두리 ─────────────────────────── */}
                    <rect x={M.left} y={M.top} width={CW} height={CH}
                        fill="none" stroke={C.axisLine} strokeWidth="1.2" rx="3" />

                    {/* ── 데이터 포인트: LL / DUL / SAT ──────── */}
                    {soilTestList.map((s, i) => (
                        <g key={`pts-${i}`}>
                            {mkPt(xScale(s.droughtPoint),   yScale(s.depth), C.ll,  'LL (위조점)',      s.droughtPoint,    s.depth)}
                            {mkPt(xScale(s.drainLimitPoint),yScale(s.depth), C.dul, 'DUL (포장용수량)', s.drainLimitPoint, s.depth)}
                            {mkPt(xScale(s.floodPoint),     yScale(s.depth), C.sat, 'SAT (포화수분)',   s.floodPoint,      s.depth)}
                        </g>
                    ))}

                    {/* ── 데이터 포인트: SW ───────────────────── */}
                    {swPts.slice(1).map((pt, i) => {
                        const depth = SW_LAYER_DEPTHS[i];
                        const val   = swVals[i];
                        if (val === undefined || depth > Y_MAX) return null;
                        return mkPt(pt[0], pt[1], C.sw, 'SW (현재 토양 수분)', val, depth);
                    })}

                    {/* ── X축 상단 ────────────────────────────── */}
                    <line x1={M.left} y1={M.top} x2={M.left + CW} y2={M.top}
                        stroke={C.axisLine} strokeWidth="1.5" />
                    {X_TICKS.map(t => (
                        <g key={`xt-${t}`}>
                            <line x1={xScale(t)} y1={M.top}
                                  x2={xScale(t)} y2={M.top - 6}
                                stroke={C.axisTick} strokeWidth="1.2" />
                            <text x={xScale(t)} y={M.top - 10}
                                textAnchor="middle" fontSize="10.5"
                                fill={C.axisLabel} fontWeight="500">
                                {t.toFixed(1)}
                            </text>
                        </g>
                    ))}
                    {/* X축 타이틀 */}
                    <text x={M.left + CW / 2} y={M.top - 32}
                        textAnchor="middle" fontSize="12" fontWeight="700"
                        fill={C.axisTitle} letterSpacing="0.3">
                        체적수분함량 (mm³/mm³)
                    </text>

                    {/* ── Y축 좌측 ────────────────────────────── */}
                    <line x1={M.left} y1={M.top} x2={M.left} y2={M.top + CH}
                        stroke={C.axisLine} strokeWidth="1.5" />
                    {yTicks.map(t => (
                        <g key={`yt-${t}`}>
                            <line x1={M.left - 6} y1={yScale(t)}
                                  x2={M.left} y2={yScale(t)}
                                stroke={C.axisTick} strokeWidth="1.2" />
                            <text x={M.left - 10} y={yScale(t) + 4}
                                textAnchor="end" fontSize="10.5"
                                fill={C.axisLabel} fontWeight="500">
                                {t}
                            </text>
                        </g>
                    ))}
                    {/* Y축 타이틀 */}
                    <text
                        transform={`translate(15,${M.top + CH / 2}) rotate(-90)`}
                        textAnchor="middle" fontSize="12" fontWeight="700"
                        fill={C.axisTitle} letterSpacing="0.3">
                        Depth (cm)
                    </text>

                    {/* ── 툴팁 ────────────────────────────────── */}
                    {renderTooltip()}

                    {/* ── 범례 Row 1: LL / DUL / SAT / PAWC ─── */}
                    <g transform={`translate(${M.left},${legendY})`}>
                        {mkLegendLine(0,   13, C.ll,  'LL (위조점)')}
                        {mkLegendLine(110, 13, C.dul, 'DUL (포장용수량)')}
                        {mkLegendLine(255, 13, C.sat, 'SAT (포화수분)', '9,5')}
                        {mkLegendRect(390, 13, C.dul, 'PAWC 범위')}
                    </g>

                    {/* ── 범례 Row 2: SW (있을 때만) ──────────── */}
                    {hasSw && (
                        <g transform={`translate(${M.left},${legendY + 30})`}>
                            {mkLegendLine(0, 13, C.sw, 'SW (현재 토양 수분 상태)', '13,6')}
                        </g>
                    )}
                </svg>
            </div>
        </div>
    );
};

export default GrowthDashboardPawcCard;
